import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Table,
  TableBody,

  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Download, RefreshCw, BarChart3, PieChart, Filter } from "lucide-react";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { format } from "date-fns";
import { getBackendUrl } from "../config/env";

interface QuestionStatistics {
  surveyId: string;
  surveyTitle: string;
  questionId: string;
  questionText: string;
  questionType: string;
  options: {
    [key: string]: {
      count: number;
      percentage: number;
    };
  };
  totalResponses: number;
}

interface Category {
  id: string;
  name: string;
  description?: string;
}

interface Survey {
  id: string;
  title: string;
  categoryId?: string;
}

const StatisticsData: React.FC = () => {
  const { user, centerInfo, selectedCenterId } = useAuth();
  const [statistics, setStatistics] = useState<QuestionStatistics[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("all");
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"table" | "chart">("table");

  const fetchCategories = useCallback(async () => {
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      const url = centerUserId
        ? `${getBackendUrl()}/api/survey-categories?isActive=true&centerUserId=${centerUserId}`
        : `${getBackendUrl()}/api/survey-categories?isActive=true`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [selectedCenterId, user?.centerId, centerInfo?.centerId]);

  const fetchSurveys = useCallback(async () => {
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      
      // 모든 설문조사를 가져오기 위해 limit을 크게 설정
      const params = new URLSearchParams({
        page: "1",
        limit: "1000",
      });
      
      if (centerUserId) {
        params.append("centerUserId", centerUserId);
      }
      
      const url = `${getBackendUrl()}/api/surveys?${params.toString()}`;
      
      console.log("🔍 StatisticsData API 요청:", url);

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        
        console.log("📊 받은 설문조사 데이터:", data);
        
        // 페이징 응답과 비-페이징 응답 모두 처리
        const surveys = data.data ? data.data : data;
        console.log("📋 처리된 설문조사 목록:", surveys);
        
        setSurveys(surveys);
      }
    } catch (error) {
      console.error("Error fetching surveys:", error);
      // 오류 시 안전한 초기값 설정
      setSurveys([]);
    }
  }, [selectedCenterId, user?.centerId, centerInfo?.centerId]);

  const fetchStatistics = useCallback(async () => {
    // Don't fetch if no survey is selected
    if (selectedSurveyId === "all") {
      setStatistics([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      let url = `${getBackendUrl()}/api/analytics/response-statistics?`;
      const params = new URLSearchParams();

      // Only pass surveyId since that's what we're filtering by
      params.append("surveyId", selectedSurveyId);

      if (centerUserId) {
        params.append("centerUserId", centerUserId);
      }

      const response = await fetch(url + params.toString());
      if (response.ok) {
        const data = await response.json();
        setStatistics(data.statistics || []);
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
      setStatistics([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSurveyId, selectedCenterId, user?.centerId, centerInfo?.centerId]);

  useEffect(() => {
    fetchCategories();
    fetchSurveys();
  }, [fetchCategories, fetchSurveys]);

  useEffect(() => {
    // Only fetch statistics if a specific survey is selected
    if (selectedSurveyId !== "all") {
      fetchStatistics();
    } else {
      setStatistics([]);
      setLoading(false);
    }
  }, [selectedSurveyId, fetchStatistics]);

  const exportToCSV = () => {
    let csvContent = "\uFEFF"; // BOM for Excel Korean compatibility

    // Headers
    csvContent += "설문조사,질문,질문유형,응답옵션,응답수,비율(%),총응답수\n";

    // Data
    statistics.forEach((stat) => {
      Object.keys(stat.options).forEach((option) => {
        const optionData = stat.options[option];
        csvContent += `"${stat.surveyTitle}","${stat.questionText}","${stat.questionType}","${option}",${optionData.count},${optionData.percentage},${stat.totalResponses}\n`;
      });
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `response_statistics_${format(new Date(), "yyyyMMdd_HHmmss")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = async () => {
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;
      let url = `${getBackendUrl()}/api/analytics/export/excel?`;
      const params = new URLSearchParams();

      if (selectedSurveyId !== "all") {
        params.append("surveyId", selectedSurveyId);
      }

      if (selectedCategoryId !== "all") {
        params.append("categoryId", selectedCategoryId);
      }

      if (centerUserId) {
        params.append("centerUserId", centerUserId);
      }

      const response = await fetch(url + params.toString());

      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `survey_analytics_${format(new Date(), "yyyyMMdd_HHmmss")}.xlsx`
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        console.error("Failed to export to Excel");
      }
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    }
  };

  const getQuestionTypeLabel = (type: string): string => {
    const typeLabels: { [key: string]: string } = {
      "multiple-choice": "객관식",
      checkbox: "체크박스",
      rating: "평점",
      dropdown: "드롭다운",
    };
    return typeLabels[type] || type;
  };

  const getQuestionTypeBadgeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      "multiple-choice": "bg-blue-100 text-blue-800",
      checkbox: "bg-green-100 text-green-800",
      rating: "bg-yellow-100 text-yellow-800",
      dropdown: "bg-purple-100 text-purple-800",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  // Filter surveys based on selected category
  const filteredSurveys = Array.isArray(surveys) 
    ? (selectedCategoryId === "all"
        ? surveys
        : surveys.filter((s) => s.categoryId === selectedCategoryId))
    : [];

  // Group statistics by survey and question for better visualization
  const groupedStatistics = statistics.reduce((acc, stat) => {
    const key = `${stat.surveyId}_${stat.questionId}`;
    if (!acc[key]) {
      acc[key] = stat;
    }
    return acc;
  }, {} as { [key: string]: QuestionStatistics });

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>응답 통계 데이터</CardTitle>
              <CardDescription>
                카테고리 및 설문조사별 응답 통계를 확인하세요
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchStatistics} variant="outline" size="icon">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                onClick={exportToCSV}
                variant="outline"
                disabled={statistics.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                CSV 내보내기
              </Button>
              <Button
                onClick={exportToExcel}
                variant="outline"
                disabled={selectedSurveyId === "all"}
              >
                <Download className="h-4 w-4 mr-2" />
                Excel 내보내기
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">
                카테고리 (설문조사 필터링)
              </label>
              <Select
                value={selectedCategoryId}
                onValueChange={(value) => {
                  setSelectedCategoryId(value);
                  setSelectedSurveyId("all"); // Reset survey selection when category changes
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="카테고리 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">모든 카테고리</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">
                설문조사 <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedSurveyId}
                onValueChange={setSelectedSurveyId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="설문조사를 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" disabled>
                    설문조사를 선택하세요
                  </SelectItem>
                  {filteredSurveys.length === 0 ? (
                    <SelectItem value="none" disabled>
                      {selectedCategoryId !== "all"
                        ? "이 카테고리에 설문조사가 없습니다"
                        : "설문조사가 없습니다"}
                    </SelectItem>
                  ) : (
                    filteredSurveys.map((survey) => (
                      <SelectItem key={survey.id} value={survey.id}>
                        {survey.title}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <Button
                  size="sm"
                  variant={viewMode === "table" ? "default" : "ghost"}
                  onClick={() => setViewMode("table")}
                  className="px-3"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  테이블
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === "chart" ? "default" : "ghost"}
                  onClick={() => setViewMode("chart")}
                  className="px-3"
                >
                  <PieChart className="h-4 w-4 mr-2" />
                  차트
                </Button>
              </div>
            </div>
          </div>

          {/* Statistics Display */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-gray-500">통계를 불러오는 중...</div>
            </div>
          ) : selectedSurveyId === "all" ? (
            <div className="text-center py-16">
              <Filter className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                설문조사를 선택해주세요
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                통계를 확인하려면 특정 설문조사를 선택해야 합니다.
              </p>
              {selectedCategoryId !== "all" && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  선택한 카테고리:{" "}
                  {categories.find((c) => c.id === selectedCategoryId)?.name}
                </p>
              )}
            </div>
          ) : statistics.length === 0 ? (
            <div className="text-center py-16">
              <Filter className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                통계 데이터가 없습니다
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                선택한 필터에 해당하는 응답 데이터가 없습니다.
              </p>
            </div>
          ) : viewMode === "table" ? (
            <div className="space-y-6">
              {Object.values(groupedStatistics).map((stat) => (
                <Card
                  key={`${stat.surveyId}_${stat.questionId}`}
                  className="overflow-hidden"
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold text-base mb-1">
                          {stat.surveyTitle}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {stat.questionText}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Badge
                          className={getQuestionTypeBadgeColor(
                            stat.questionType
                          )}
                        >
                          {getQuestionTypeLabel(stat.questionType)}
                        </Badge>
                        <Badge variant="outline">
                          총 {stat.totalResponses}개 응답
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>응답 옵션</TableHead>
                          <TableHead className="text-center w-32">
                            응답 수
                          </TableHead>
                          <TableHead className="text-center w-32">
                            비율
                          </TableHead>
                          <TableHead className="w-64">분포</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(stat.options)
                          .sort((a, b) => b[1].count - a[1].count)
                          .map(([option, data]) => (
                            <TableRow key={option}>
                              <TableCell className="font-medium">
                                {option}
                              </TableCell>
                              <TableCell className="text-center">
                                {data.count}
                              </TableCell>
                              <TableCell className="text-center">
                                {data.percentage}%
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Progress
                                    value={data.percentage}
                                    className="flex-1"
                                  />
                                  <span className="text-xs text-gray-500 w-12 text-right">
                                    {data.percentage}%
                                  </span>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.values(groupedStatistics).map((stat) => (
                <Card key={`${stat.surveyId}_${stat.questionId}`}>
                  <CardHeader className="pb-3">
                    <h4 className="font-semibold text-base mb-1">
                      {stat.surveyTitle}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {stat.questionText}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge
                        className={getQuestionTypeBadgeColor(stat.questionType)}
                      >
                        {getQuestionTypeLabel(stat.questionType)}
                      </Badge>
                      <Badge variant="outline">
                        총 {stat.totalResponses}개 응답
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(stat.options)
                        .sort((a, b) => b[1].count - a[1].count)
                        .map(([option, data]) => (
                          <div key={option}>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium">
                                {option}
                              </span>
                              <span className="text-sm text-gray-500">
                                {data.count}개 ({data.percentage}%)
                              </span>
                            </div>
                            <Progress value={data.percentage} className="h-2" />
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StatisticsData;
